// client/src/components/clientes/FidelidadeCliente.js
import React, { useState } from 'react';
import { Loader2, Coins, Gift, ArrowUpCircle, ArrowDownCircle, RotateCcw, Award, AlertCircle } from 'lucide-react';
import Pagination from '../common/Pagination';
import {
    useSaldoFidelidade,
    useExtratoFidelidade,
    useResgatarDesconto,
    useResgatarPremio,
    usePremiosFidelidade,
    useFidelidadeConfig,
} from '../../hooks/useFidelidade';

const TIPO_ICON = {
    ganho: { Icon: ArrowUpCircle, color: '#16a34a' },
    resgate_desconto: { Icon: ArrowDownCircle, color: '#dc2626' },
    resgate_premio: { Icon: Gift, color: '#dc2626' },
    ajuste_manual: { Icon: Coins, color: '#64748b' },
    estorno: { Icon: RotateCcw, color: '#16a34a' },
};

const FidelidadeCliente = ({ clienteId }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const porPagina = 8;

    const { data: fidelidadeConfig, isLoading: configLoading, isError: configIsError, error: configErrorObj } = useFidelidadeConfig();
    const { data: saldo, isLoading: saldoLoading, isError: saldoIsError, error: saldoErrorObj } = useSaldoFidelidade(clienteId);
    const { data: extratoData, isLoading: extratoLoading, isError: extratoIsError, error: extratoErrorObj } = useExtratoFidelidade(clienteId, { page: currentPage, limit: porPagina });
    const { data: premios = [] } = usePremiosFidelidade({ ativo: true });

    const resgatarDesconto = useResgatarDesconto(clienteId);
    const resgatarPremio = useResgatarPremio(clienteId);

    const [pontosParaResgatar, setPontosParaResgatar] = useState('');

    if (configLoading || saldoLoading) {
        return (
            <div className="historico-container">
                <div className="loading-state" style={{ height: '200px' }}>
                    <Loader2 className="animate-spin" /> Carregando fidelidade...
                </div>
            </div>
        );
    }

    if (configIsError || saldoIsError) {
        const mensagem = (configErrorObj || saldoErrorObj)?.response?.data?.error
            || (configErrorObj || saldoErrorObj)?.response?.data?.message
            || 'Erro ao carregar dados de fidelidade deste cliente.';
        return (
            <div className="historico-container">
                <div className="placeholder-content" style={{ marginTop: '1rem' }}>
                    <AlertCircle size={48} className="placeholder-icon" style={{ color: '#dc2626' }} />
                    <h4>Não foi possível carregar</h4>
                    <p>{mensagem}</p>
                </div>
            </div>
        );
    }

    if (!fidelidadeConfig?.ativo) {
        return (
            <div className="historico-container">
                <div className="placeholder-content" style={{ marginTop: '1rem' }}>
                    <Gift size={48} className="placeholder-icon" />
                    <h4>Programa de fidelidade desativado</h4>
                    <p>Ative o programa em Configurações → Fidelidade para começar a dar pontos aos clientes.</p>
                </div>
            </div>
        );
    }

    const extrato = extratoData?.data || [];
    const totalExtrato = extratoData?.total || 0;
    const valorPrevisto = ((Number(pontosParaResgatar) || 0) * (fidelidadeConfig.valorPontoEmReais || 0)).toFixed(2);

    const handleResgatarDesconto = (e) => {
        e.preventDefault();
        const pontos = Number(pontosParaResgatar);
        if (!pontos || pontos <= 0) return;
        resgatarDesconto.mutate({ pontos }, { onSuccess: () => setPontosParaResgatar('') });
    };

    return (
        <div className="historico-container">
            <div className="resumo-agendamentos-grid">
                <div className="resumo-metric-card">
                    <Coins className="metric-icon" />
                    <div>
                        <div className="metric-value">{saldo ?? 0}</div>
                        <div className="metric-label">Pontos disponíveis</div>
                    </div>
                </div>
            </div>

            <div className="form-section" style={{ marginTop: '1.5rem' }}>
                <h4 className="form-section-title">Resgatar por desconto</h4>
                <form onSubmit={handleResgatarDesconto} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div className="form-group" style={{ marginBottom: 0, minWidth: 160 }}>
                        <label className="form-label">Pontos a resgatar</label>
                        <input
                            type="number"
                            min="1"
                            max={saldo ?? 0}
                            className="form-input"
                            value={pontosParaResgatar}
                            onChange={(e) => setPontosParaResgatar(e.target.value)}
                        />
                    </div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginBottom: 12 }}>
                        = R$ {valorPrevisto} de desconto
                    </div>
                    <button
                        type="submit"
                        className="action-button action-button-primary"
                        disabled={resgatarDesconto.isPending || !pontosParaResgatar || Number(pontosParaResgatar) > (saldo ?? 0)}
                    >
                        {resgatarDesconto.isPending ? 'Resgatando...' : 'Resgatar'}
                    </button>
                </form>
                <p className="form-hint" style={{ color: 'var(--color-text-secondary)', fontSize: '0.8rem', marginTop: 8 }}>
                    O valor do desconto precisa ser aplicado manualmente na venda em andamento.
                </p>
            </div>

            {premios.length > 0 && (
                <div className="form-section">
                    <h4 className="form-section-title">Resgatar prêmio do catálogo</h4>
                    <div className="form-grid-container-cols-2">
                        {premios.map((premio) => {
                            const podeResgatar = (saldo ?? 0) >= premio.custoPontos && (premio.quantidadeDisponivel == null || premio.quantidadeDisponivel > 0);
                            return (
                                <div key={premio._id} className="content-panel" style={{ padding: '0.9rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <Award size={14} /> {premio.nome}
                                        </div>
                                        <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>{premio.custoPontos} pontos</div>
                                    </div>
                                    <button
                                        onClick={() => resgatarPremio.mutate(premio._id)}
                                        className="action-button action-button-secondary"
                                        style={{ fontSize: '0.75rem', padding: '4px 10px', whiteSpace: 'nowrap' }}
                                        disabled={!podeResgatar || resgatarPremio.isPending}
                                        title={!podeResgatar ? 'Pontos insuficientes ou prêmio esgotado' : undefined}
                                    >
                                        Resgatar
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="form-section">
                <h4 className="form-section-title">Extrato de pontos</h4>
                {extratoLoading && extrato.length === 0 ? (
                    <div className="loading-state"><Loader2 className="animate-spin" /></div>
                ) : extratoIsError ? (
                    <div className="placeholder-content">
                        <AlertCircle size={40} className="placeholder-icon" style={{ color: '#dc2626' }} />
                        <p>{extratoErrorObj?.response?.data?.error || extratoErrorObj?.response?.data?.message || 'Erro ao carregar o extrato.'}</p>
                    </div>
                ) : extrato.length === 0 ? (
                    <div className="placeholder-content">
                        <Coins size={40} className="placeholder-icon" />
                        <p>Nenhuma movimentação de pontos ainda.</p>
                    </div>
                ) : (
                    <ul className="agenda-list">
                        {extrato.map((t) => {
                            const { Icon, color } = TIPO_ICON[t.tipo] || TIPO_ICON.ajuste_manual;
                            return (
                                <li key={t._id} className="agenda-list-item">
                                    <div className="item-time" style={{ fontSize: '0.8rem' }}>
                                        {new Date(t.createdAt).toLocaleDateString('pt-BR')}
                                    </div>
                                    <div className="item-details">
                                        <h4 className="item-title"><Icon size={14} className="icon" style={{ color }} />{t.descricao || t.tipo}</h4>
                                    </div>
                                    <div className="item-meta">
                                        <div className="item-price" style={{ color: t.pontos >= 0 ? '#16a34a' : '#dc2626' }}>
                                            {t.pontos >= 0 ? '+' : ''}{t.pontos} pts
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}

                {totalExtrato > porPagina && (
                    <Pagination itemsPerPage={porPagina} totalItems={totalExtrato} currentPage={currentPage} onPageChange={setCurrentPage} />
                )}
            </div>
        </div>
    );
};

export default FidelidadeCliente;
